import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
    LayoutDashboard, 
    ChefHat, 
    Users, 
    UsersRound,
    ClipboardCheck, 
    Award, 
    ListChecks, 
    UserCog,
    LogOut,
    GraduationCap,
    BarChart3,
    Archive,
    Trash2,
    Settings,
    FileText,
    X,
    Moon,
    Sun,
    Zap,
    ClipboardList,
    BookOpenCheck,
    SlidersHorizontal
} from 'lucide-react';

const Sidebar = ({ onClose }) => {
    const { user, logout, isAdmin, API } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [logo, setLogo] = useState(null);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const response = await axios.get(`${API}/settings/logo`);
                if (response.data.logo) setLogo(response.data.logo);
            } catch (error) { /* ignore */ }
        };
        fetchLogo();
    }, [API]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/lessons', icon: ChefHat, label: 'Lessons' },
        { to: '/assessments', icon: FileText, label: 'Assessments' },
        { to: '/skill-assessments', icon: Zap, label: 'Skill Assessments' },
        { to: '/poe', icon: BookOpenCheck, label: 'Portfolio of Evidence' },
        { to: '/students', icon: GraduationCap, label: 'Students' },
        { to: '/groups', icon: UsersRound, label: 'Groups' },
        { to: '/attendance', icon: ClipboardCheck, label: 'Attendance' },
        { to: '/grading', icon: Award, label: 'Grading' },
        { to: '/reports', icon: BarChart3, label: 'Reports' },
    ];

    const staffItems = [
        { to: '/criteria', icon: ListChecks, label: 'Criteria' },
        { to: '/assessment-forms', icon: FileText, label: 'Assessment Forms' },
        { to: '/skill-forms', icon: ClipboardList, label: 'Skill Forms' },
        { to: '/archive', icon: Archive, label: 'Archive' },
        { to: '/settings', icon: Settings, label: 'Settings' },
    ];

    const adminOnlyItems = [
        { to: '/admin/poe-setup', icon: SlidersHorizontal, label: 'POE Setup' },
        { to: '/users', icon: UserCog, label: 'Users' },
        { to: '/deletion', icon: Trash2, label: 'Deletion' },
    ];

    const navLinkClass = ({ isActive }) =>
        `flex items-center px-5 py-2.5 border-l-[3px] transition-colors text-sm ${
            isActive
                ? 'bg-gray-50 dark:bg-slate-700/50 border-ica-navy dark:border-ica-gold text-ica-navy dark:text-ica-light-gold font-semibold'
                : 'border-transparent text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700/40 hover:text-ica-navy dark:hover:text-slate-200'
        }`;

    return (
        <aside className="w-64 h-screen flex flex-col bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700" data-testid="sidebar">
            {/* Logo */}
            <div className="p-5 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md overflow-hidden">
                        {logo ? (
                            <img src={logo} alt="Logo" className="w-full h-full object-contain" data-testid="sidebar-logo" />
                        ) : (
                            <div className="w-full h-full bg-ica-gold/10 flex items-center justify-center">
                                <ChefHat className="w-6 h-6 text-ica-gold" />
                            </div>
                        )}
                    </div>
                    <div>
                        <h1 className="font-heading text-lg font-semibold text-ica-navy">Pass!</h1>
                        <p className="text-xs text-gray-400 dark:text-slate-500">ICA Attendance</p>
                    </div>
                </div>
                {onClose && (
                    <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400" data-testid="sidebar-close-btn" aria-label="Close menu">
                        <X className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* User section - below logo */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-ica-navy/10 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-ica-navy" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ica-navy truncate">{user?.name}</p>
                        <p className="text-xs capitalize text-gray-400 dark:text-slate-500">{user?.role}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleTheme}
                        data-testid="theme-toggle-btn"
                        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-ica-navy transition-colors"
                    >
                        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        <span className="text-xs">{isDark ? 'Light' : 'Dark'}</span>
                    </button>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-btn"
                        className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-ica-navy transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        <span className="text-xs">Logout</span>
                    </button>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-4 overflow-y-auto">
                <div className="px-4 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">Main</span>
                </div>
                {navItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                        className={navLinkClass}
                    >
                        <item.icon className="w-5 h-5 mr-3" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}

                {isAdmin() && (
                    <>
                        <div className="px-4 mt-6 mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">Admin</span>
                        </div>
                        {[...staffItems, ...adminOnlyItems].map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={navLinkClass}
                            >
                                <item.icon className="w-5 h-5 mr-3" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}
                {!isAdmin() && (
                    <>
                        <div className="px-4 mt-6 mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">Management</span>
                        </div>
                        {staffItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={navLinkClass}
                            >
                                <item.icon className="w-5 h-5 mr-3" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}
            </nav>

        </aside>
    );
};

export default Sidebar;
